// RadicalPresetSync.js
import { supabase } from "../supabaseClient";
import {
    createUserPresetId,
    normalizeUserPreset,
} from "../components/RadicalHelpers";

const TABLE = "radical_presets";
const SYNC_KEY = "radical-screen-preset-sync-v1";

const safeObject = (value) =>
    value && typeof value === "object" && !Array.isArray(value) ? value : {};

const readSyncState = () => {
    try {
        const value = JSON.parse(localStorage.getItem(SYNC_KEY));

        return {
            pending: safeObject(value?.pending),
            users: safeObject(value?.users),
        };
    } catch (error) {
        return {
            pending: {},
            users: {},
        };
    }
};

const writeSyncState = (value) => {
    localStorage.setItem(SYNC_KEY, JSON.stringify(value));
};

const getUserMetadata = (state, userId) => {
    state.users[userId] = {
        presets: safeObject(state.users[userId]?.presets),
    };

    return state.users[userId].presets;
};

const normalizePresets = (presets) =>
    (Array.isArray(presets) ? presets : [])
        .map(normalizeUserPreset)
        .filter(Boolean);

const sortObject = (value) => {
    if (Array.isArray(value)) {
        return value.map(sortObject);
    }

    if (!value || typeof value !== "object") {
        return value;
    }

    return Object.keys(value)
        .sort()
        .reduce((result, key) => {
            result[key] = sortObject(value[key]);
            return result;
        }, {});
};

const presetsAreEqual = (first, second) =>
    JSON.stringify(sortObject(normalizeUserPreset(first))) ===
    JSON.stringify(sortObject(normalizeUserPreset(second)));

const createConflictPreset = (preset, suffix) => ({
    ...normalizeUserPreset(preset),
    id: createUserPresetId(),
    name: `${preset.name || "Guardado"} (${suffix})`,
    isBuiltIn: false,
});

const saveRemotePreset = async (userId, preset) => {
    const normalized = normalizeUserPreset(preset);

    const { data, error } = await supabase
        .from(TABLE)
        .upsert(
            {
                user_id: userId,
                preset_id: normalized.id,
                data: normalized,
                deleted_at: null,
            },
            {
                onConflict: "user_id,preset_id",
            },
        )
        .select("preset_id,data,updated_at,deleted_at")
        .single();

    if (error) {
        throw error;
    }

    return data;
};

const deleteRemotePreset = async (userId, presetId) => {
    const { data, error } = await supabase
        .from(TABLE)
        .update({
            deleted_at: new Date().toISOString(),
        })
        .eq("user_id", userId)
        .eq("preset_id", presetId)
        .select("preset_id,data,updated_at,deleted_at")
        .maybeSingle();

    if (error) {
        throw error;
    }

    if (!data) {
        throw new Error(`No se pudo eliminar el preset ${presetId}.`);
    }

    return data;
};

const setCleanMetadata = (metadata, row) => {
    metadata[row.preset_id] = {
        dirty: false,
        deletedAt: row.deleted_at || null,
        remoteUpdatedAt: row.updated_at,
    };
};

export const createRadicalPresetSync = ({
    getPresets,
    onPresetsChange,
    onError,
    onSynced,
    refreshMs = 30000,
}) => {
    let activeUserId = null;
    let authSubscription = null;
    let stopped = false;
    let running = false;
    let rerun = false;
    let lastSyncAt = 0;
    let currentPromise = Promise.resolve(null);

    const mark = (presetIds, deleted = false) => {
        const state = readSyncState();
        const metadata = activeUserId
            ? getUserMetadata(state, activeUserId)
            : state.pending;
        const now = new Date().toISOString();

        [...new Set(presetIds)].filter(Boolean).forEach((presetId) => {
            metadata[presetId] = {
                ...safeObject(metadata[presetId]),
                dirty: true,
                deletedAt: deleted ? now : null,
            };
        });

        writeSyncState(state);
    };

    const sync = async () => {
        const localPresets = normalizePresets(getPresets());

        const {
            data: { session },
            error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
            throw sessionError;
        }

        if (!session?.user?.id) {
            activeUserId = null;

            return {
                authenticated: false,
                presets: localPresets,
            };
        }

        const userId = session.user.id;

        activeUserId = userId;

        const state = readSyncState();
        const metadata = getUserMetadata(state, userId);

        Object.entries(state.pending).forEach(([presetId, value]) => {
            metadata[presetId] = {
                ...safeObject(metadata[presetId]),
                ...safeObject(value),
                dirty: true,
            };
        });

        state.pending = {};

        const localMap = new Map(
            localPresets.map((preset) => [preset.id, preset]),
        );

        localPresets.forEach((preset) => {
            if (!metadata[preset.id]) {
                metadata[preset.id] = {
                    dirty: true,
                    deletedAt: null,
                    remoteUpdatedAt: null,
                };
            }
        });

        const result = {
            authenticated: true,
            uploaded: 0,
            downloaded: 0,
            deleted: 0,
            conflicts: 0,
        };

        try {
            const { data: rows, error } = await supabase
                .from(TABLE)
                .select("preset_id,data,updated_at,deleted_at")
                .eq("user_id", userId);

            if (error) {
                throw error;
            }

            const remoteMap = new Map(
                (rows || []).map((row) => [row.preset_id, row]),
            );

            const ids = new Set([
                ...localMap.keys(),
                ...remoteMap.keys(),
                ...Object.keys(metadata),
            ]);

            for (const presetId of ids) {
                const local = localMap.get(presetId);
                const remote = remoteMap.get(presetId);
                const meta = safeObject(metadata[presetId]);

                if (remote?.deleted_at) {
                    if (local && meta.dirty) {
                        const conflict = createConflictPreset(
                            local,
                            "conflicto local",
                        );

                        const conflictRow = await saveRemotePreset(
                            userId,
                            conflict,
                        );

                        localMap.set(conflict.id, conflict);
                        setCleanMetadata(metadata, conflictRow);

                        result.uploaded += 1;
                        result.conflicts += 1;
                    }

                    localMap.delete(presetId);
                    setCleanMetadata(metadata, remote);
                    continue;
                }

                if (!remote) {
                    if (meta.deletedAt || !local) {
                        delete metadata[presetId];
                        continue;
                    }

                    const row = await saveRemotePreset(userId, local);

                    setCleanMetadata(metadata, row);
                    result.uploaded += 1;
                    continue;
                }

                const remotePreset = normalizeUserPreset({
                    ...safeObject(remote.data),
                    id: remote.preset_id,
                });

                if (!remotePreset) {
                    continue;
                }

                if (!local) {
                    if (meta.deletedAt) {
                        if (
                            !meta.remoteUpdatedAt ||
                            meta.remoteUpdatedAt !== remote.updated_at
                        ) {
                            const conflict = createConflictPreset(
                                remotePreset,
                                "conflicto remoto",
                            );

                            const conflictRow = await saveRemotePreset(
                                userId,
                                conflict,
                            );

                            localMap.set(conflict.id, conflict);
                            setCleanMetadata(metadata, conflictRow);

                            result.uploaded += 1;
                            result.conflicts += 1;
                        }

                        const row = await deleteRemotePreset(userId, presetId);

                        setCleanMetadata(metadata, row);
                        result.deleted += 1;
                        continue;
                    }

                    localMap.set(presetId, remotePreset);
                    setCleanMetadata(metadata, remote);
                    result.downloaded += 1;
                    continue;
                }

                if (presetsAreEqual(local, remotePreset)) {
                    setCleanMetadata(metadata, remote);
                    continue;
                }

                if (meta.dirty && meta.remoteUpdatedAt === remote.updated_at) {
                    const row = await saveRemotePreset(userId, local);

                    setCleanMetadata(metadata, row);
                    result.uploaded += 1;
                    continue;
                }

                if (meta.dirty) {
                    const conflict = createConflictPreset(
                        local,
                        "conflicto local",
                    );

                    const conflictRow = await saveRemotePreset(
                        userId,
                        conflict,
                    );

                    localMap.set(presetId, remotePreset);
                    localMap.set(conflict.id, conflict);

                    setCleanMetadata(metadata, remote);
                    setCleanMetadata(metadata, conflictRow);

                    result.uploaded += 1;
                    result.downloaded += 1;
                    result.conflicts += 1;
                    continue;
                }

                localMap.set(presetId, remotePreset);
                setCleanMetadata(metadata, remote);
                result.downloaded += 1;
            }
        } finally {
            writeSyncState(state);
        }

        return {
            ...result,
            presets: Array.from(localMap.values()),
        };
    };

    const syncNow = (force = true) => {
        if (stopped) {
            return Promise.resolve(null);
        }

        if (!force && (running || Date.now() - lastSyncAt < refreshMs)) {
            return currentPromise;
        }

        if (running) {
            rerun = true;
            return currentPromise;
        }

        running = true;

        currentPromise = (async () => {
            let result = null;

            do {
                rerun = false;
                result = await sync();

                if (!stopped && result.authenticated) {
                    onPresetsChange(result.presets);
                    onSynced?.(result);
                }

                lastSyncAt = Date.now();
            } while (rerun && !stopped);

            return result;
        })()
            .catch((error) => {
                onError?.(error);
                return null;
            })
            .finally(() => {
                running = false;
            });

        return currentPromise;
    };

    const handleFocus = () => {
        syncNow(false);
    };

    const handleVisibility = () => {
        if (document.visibilityState === "visible") {
            syncNow(false);
        }
    };

    const start = async () => {
        stopped = false;

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            activeUserId = session?.user?.id || null;

            if (event === "INITIAL_SESSION" || event === "SIGNED_IN") {
                window.setTimeout(() => {
                    syncNow(false);
                }, 0);
            }
        });

        authSubscription = subscription;

        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibility);

        const {
            data: { session },
            error,
        } = await supabase.auth.getSession();

        if (error) {
            onError?.(error);
            return;
        }

        activeUserId = session?.user?.id || null;

        if (activeUserId) {
            syncNow(false);
        }
    };

    const stop = () => {
        stopped = true;

        authSubscription?.unsubscribe();

        window.removeEventListener("focus", handleFocus);
        document.removeEventListener("visibilitychange", handleVisibility);
    };

    return {
        start,
        stop,
        syncNow,

        presetChanged: (presetId) => {
            mark([presetId]);
            return syncNow();
        },

        presetsChanged: (presetIds) => {
            mark(presetIds);
            return syncNow();
        },

        presetDeleted: (presetId) => {
            mark([presetId], true);
            return syncNow();
        },
    };
};
